// src/components/admin/page-sections/SlideshowSection.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const SlideshowSection = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Extract content or use defaults
  const { 
    slides = [],
    height = '800px',
    overlayOpacity = '0.6',
    textAlignment = 'center',
    transitionSpeed = 5000,
    animationStyle = 'fade',
    buttonText,
    buttonLink,
    buttonStyle = 'primary'
  } = content;

  // If no slides were specified, create a placeholder
  const slidesToUse = slides.length > 0 ? slides : [
    {
      imageUrl: '/images/s3.jpg',
      title: 'Transforming Packaging, Elevating Brands',
      subtitle: 'SINCE 1998',
      text: 'Expert packaging solutions that enhance your brand identity and make a lasting impression'
    }
  ];

  // Slide transition logic
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slidesToUse.length);
  }, [slidesToUse.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slidesToUse.length) % slidesToUse.length);
  }, [slidesToUse.length]);

  // Auto-advance slides
  useEffect(() => {
    if (slidesToUse.length <= 1) return; // Don't auto-advance if only one slide
    
    const slideInterval = setInterval(nextSlide, transitionSpeed);
    return () => clearInterval(slideInterval);
  }, [nextSlide, transitionSpeed, slidesToUse.length]);

  // Determine text alignment classes
  const alignmentClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end'
  };

  // Determine button classes
  const buttonClasses = {
    primary: 'bg-white text-[#033568] hover:bg-gray-100',
    secondary: 'bg-amber-700 text-white hover:bg-amber-600',
    outline: 'bg-transparent border-2 border-white text-white hover:bg-white/10',
    link: 'bg-transparent text-white hover:underline'
  };

  return (
    <div 
      className="relative w-full overflow-hidden group"
      style={{ height }}
    >
      {slidesToUse.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {/* The slide image */}
          <img
            src={slide.imageUrl}
            alt={slide.imageAlt || `Slide ${index + 1}`}
            className="w-full h-full object-cover scale-105 transform-gpu z-0"
            style={{
              transform: index === currentSlide ? 'scale(1.0)' : 'scale(1.1)',
              transition: 'transform 7s ease-out'
            }}
            loading="lazy"
          />

          {/* Dark overlay on top of the image */}
          <div 
            className="absolute inset-0 bg-black" 
            style={{ opacity: overlayOpacity }}
          />

          {/* Text content on top of the overlay */}
          <div className={`absolute inset-0 flex flex-col ${alignmentClasses[textAlignment]} justify-center px-4 z-20`}>
            {slide.subtitle && (
              <span className="inline-block py-2 px-6 rounded-full bg-[#033568] text-white text-sm font-bold mb-4 opacity-0 animate-fadeIn">
                {slide.subtitle}
              </span>
            )}
            
            {slide.title && (
            //   <h2 className="text-white text-4xl md:text-6xl font-bold drop-shadow-lg max-w-4xl mx-auto mb-6 leading-tight opacity-0 animate-slideUp">
            //     {slide.title}
            //   </h2>
            <h2 className="text-white text-4xl md:text-6xl font-bold drop-shadow-lg max-w-4xl mx-auto mb-6 leading-tight opacity-0 animate-slideUp"> 
            {(() => { 
                // Split the title into words 
                const words = slide.title.split(' ');
                // Group words into chunks of 2 words each 
                const groups = [];
                for (let i = 0; i < words.length; i += 2) { 
                    groups.push(words.slice(i, i + 2).join(' ')); 
                } 
                // Map through each group:
                //  if its index is odd, wrap with a span having amber class
                return groups.map((group, index) => index % 2 === 1 ? 
                    <span key={index} className="text-amber-700">{group} </span> : 
                    <span key={index}>{group} </span>
                );
             })()}
            </h2>
            )}
            
            {slide.text && (
              <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8 opacity-0 animate-slideUp animation-delay-200">
                {slide.text}
              </p>
            )}
            
            {buttonText && buttonLink && (
              <div className="opacity-0 animate-fadeIn animation-delay-400">
                <Link
                  to={buttonLink}
                  className={`inline-flex items-center py-3 px-8 ${buttonClasses[buttonStyle]} rounded-full font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105`}
                >
                  {buttonText}
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Only show navigation if more than one slide */}
      {slidesToUse.length > 1 && (
        <>
          {/* Slide Navigation Controls */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-4 rounded-full transition-all z-20 opacity-0 group-hover:opacity-100 transform hover:scale-110"
            aria-label="Previous Slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-4 rounded-full transition-all z-20 opacity-0 group-hover:opacity-100 transform hover:scale-110"
            aria-label="Next Slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex space-x-3 z-20">
            {slidesToUse.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-amber-700 w-10' 
                    : 'bg-white/50 hover:bg-white/75 w-3'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Curved bottom shape */}
      <div className="absolute -bottom-1 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path fill="#f9fafb" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,176C384,192,480,192,576,176C672,160,768,128,864,128C960,128,1056,160,1152,176C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
    </div>
  );
};

export default SlideshowSection;